import { Injectable } from '@angular/core';
import { HttpEvent, HttpResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

/**
 * Saves and provides cache from memory
 * @export
 * @class HttpCacheService
 */
@Injectable({
  providedIn: 'root',
})
export class HttpCacheService {
  private cache = new Map<string, { data: any; expiry: number }>();
  private cacheDuration = 10 * 60 * 1000; // Cache duration in milliseconds

  constructor() {}

   /**
   * Invalidate all cache entries by resetting their expiry time
   * @memberof HttpCacheService
   */
    invalidateCache(): void {         
      const now = Date.now();
      this.cache.forEach((value, key) => {
        this.cache.set(key, { data: value.data, expiry: value.expiry - this.cacheDuration });
      });
    }
  

  /**
   * Check if data exists in the cache and is still valid
   * @param {string} key
   * @return {{ data: any; expired: boolean }}
   * @memberof HttpCacheService
   */
  getCacheData(key: string): { data: any; expired: boolean } {
    const cached = this.cache.get(key);
    let expired = true; // Assume expired initially

    if (cached) {
      const now = Date.now();
      expired = now > cached.expiry; // Update expired status
    }

    return { data: cached ? cached.data : null, expired };
  }

  /**
   * Set cache validity (reset expiry time)
   * @param {string} key
   * @memberof HttpCacheService
   */
  setCacheValid(key: string): void {
    const cached = this.cache.get(key);
    if (cached) {
      const newExpiry = Date.now() + this.cacheDuration;
      this.cache.set(key, { data: cached.data, expiry: newExpiry });
    }
  }

  /**
   * Save data in the cache with a new timestamp
   * @param {string} key
   * @param {*} data
   * @memberof HttpCacheService
   */
  setCacheData(key: string, data: any): void {
    const expiry = Date.now() + this.cacheDuration;
    this.cache.set(key, { data, expiry });
  }

  /**
   * Fetch data either from cache or make a request
   * @param {string} key
   * @param {() Observable<HttpEvent<any>>} requestFn
   * @return {Observable<HttpEvent<any>>}
   * @memberof HttpCacheService
   */
  fetchWithCache(key: string, requestFn: () => Observable<HttpEvent<any>>): Observable<HttpEvent<any>> {
    const { data, expired } = this.getCacheData(key);
    
    if (data) {
      // If there is valid data, return it
      if (!expired) {        
        return of(new HttpResponse({ body: data, status: 200 })); // Return valid cached data
      } else {
        // If expired, emit expired cache data and make a request        
        return new Observable<HttpEvent<any>>(observer => {
          //observer.next(new HttpResponse({ body: data, status: 200 })); // Emit expired cache data

          requestFn().pipe(
            tap(newResponse => {
              if (newResponse !== data) {
                this.setCacheData(key, newResponse); // Cache the new response after receiving it
              } else {
                this.setCacheValid(key); // Reset cache validity
              }
            })
          ).subscribe({
            next(newResponse) {
              if (newResponse !== data) {
                observer.next(newResponse); // Emit fresh response if data is new
              }
            },
            error(err) {
              observer.error(err);
            },
            complete() {
              observer.complete();
            }
          });
        });
      }
    }

    // If no cached data, make the request
    return requestFn().pipe(
      tap(newResponse => {        
        if (newResponse instanceof HttpResponse && newResponse.status === 200 ) {
          this.setCacheData(key, newResponse); // Cache the response
        }
      })
    );
  }
}
